/**
 * Mini event bus per diagnosticare bug runtime in PWA.
 * Attivazione: aprire l'URL con ?debug=1 (persiste in localStorage).
 */

const MAX_EVENTS = 400;
const STORAGE_KEY = 'mtc_debug';
const LOG_KEY = 'mtc_debug_log';
const BOOT_KEY = 'mtc_debug_boot';
const events = loadEventsFromStorage();
const listeners = new Set();
let bootTime = loadBootTime();

function loadEventsFromStorage() {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.sessionStorage.getItem(LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_EVENTS) : [];
  } catch {
    return [];
  }
}

function loadBootTime() {
  try {
    if (typeof window === 'undefined') return Date.now();
    const raw = window.sessionStorage.getItem(BOOT_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (Number.isFinite(n) && n > 0) return n;
    const now = Date.now();
    window.sessionStorage.setItem(BOOT_KEY, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

function persistEvents() {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(LOG_KEY, JSON.stringify(events));
  } catch {}
}

export function isEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('debug')) {
      const val = params.get('debug') || '1';
      if (val === '0' || val === 'off') {
        window.localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      window.localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function disable() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export function emit(name, data) {
  if (typeof window === 'undefined') return;
  if (!isEnabled()) return;
  const entry = {
    ms: Date.now() - bootTime,
    name,
    data: data == null ? null : safeData(data),
    path: window.location.pathname + window.location.search,
  };
  events.push(entry);
  if (events.length > MAX_EVENTS) events.shift();
  persistEvents();
  listeners.forEach((fn) => {
    try {
      fn(entry);
    } catch {}
  });
}

function safeData(d) {
  try {
    if (typeof d === 'string') return d;
    return JSON.parse(JSON.stringify(d));
  } catch {
    return String(d);
  }
}

export function getEvents() {
  return events.slice();
}

export function clearEvents() {
  events.length = 0;
  bootTime = Date.now();
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(LOG_KEY, '[]');
      window.sessionStorage.setItem(BOOT_KEY, String(bootTime));
    }
  } catch {}
  listeners.forEach((fn) => {
    try {
      fn(null);
    } catch {}
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function formatEvents() {
  return events
    .map((e) => {
      const ts = String(e.ms).padStart(6, ' ');
      const data = e.data == null ? '' : ' ' + JSON.stringify(e.data);
      return `+${ts}ms  ${e.name}${data}  [${e.path}]`;
    })
    .join('\n');
}

/** Aggancia listener globali utili per diagnosi PWA. Chiamare una sola volta. */
let globalsAttached = false;
export function attachGlobalDebug() {
  if (globalsAttached || typeof window === 'undefined') return;
  globalsAttached = true;

  emit('boot', {
    standalone:
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator?.standalone === true ||
      false,
    persistedEvents: events.length,
    ua: navigator.userAgent.slice(0, 80),
  });

  window.addEventListener('focus', () => emit('window:focus'));
  window.addEventListener('blur', () => emit('window:blur'));
  window.addEventListener('pageshow', (e) => emit('pageshow', { persisted: e.persisted }));
  window.addEventListener('pagehide', (e) => emit('pagehide', { persisted: e.persisted }));
  window.addEventListener('beforeunload', () => emit('beforeunload'));
  window.addEventListener('popstate', () => emit('popstate'));
  window.addEventListener('error', (e) =>
    emit('window:error', { msg: String(e.message).slice(0, 120), src: e.filename })
  );
  window.addEventListener('unhandledrejection', (e) =>
    emit('promise:rejected', { reason: String(e.reason).slice(0, 200) })
  );

  document.addEventListener('visibilitychange', () =>
    emit('visibilitychange', { state: document.visibilityState })
  );

  document.addEventListener(
    'click',
    (e) => {
      const t = e.target;
      const card = t?.closest?.('[data-testid^="content-card-"]');
      const btn = t?.closest?.('button, a, [role="button"]');
      emit('doc:click', {
        card: card?.dataset?.testid || null,
        tag: t?.tagName || null,
        btn: btn?.getAttribute?.('aria-label') || btn?.dataset?.testid || null,
      });
    },
    true
  );

  document.addEventListener(
    'touchstart',
    (e) => {
      const t = e.target;
      const card = t?.closest?.('[data-testid^="content-card-"]');
      if (card) emit('doc:touchstart-card', { card: card.dataset.testid });
    },
    { capture: true, passive: true }
  );

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => emit('sw:controllerchange'));
    navigator.serviceWorker.addEventListener('message', (e) => {
      if (e.data?.type === 'VERSION') {
        emit('sw:version', { cache: e.data.cache });
      } else {
        emit('sw:message', { type: e.data?.type || null });
      }
    });
    navigator.serviceWorker.ready
      .then((reg) => {
        const sw = reg.active || reg.waiting || reg.installing;
        emit('sw:ready', {
          state: sw?.state || null,
          scriptURL: sw?.scriptURL ? sw.scriptURL.split('/').pop() : null,
        });
        try {
          navigator.serviceWorker.controller?.postMessage({ type: 'GET_VERSION' });
        } catch {}
      })
      .catch(() => {});
  }

  window.addEventListener(
    'message',
    (e) => {
      const origin = String(e.origin || '');
      if (!origin || origin === window.location.origin) return;
      let payload = null;
      try {
        if (typeof e.data === 'string') {
          payload = e.data.slice(0, 80);
        } else if (e.data && typeof e.data === 'object') {
          payload = JSON.stringify(e.data).slice(0, 120);
        } else {
          payload = String(e.data).slice(0, 80);
        }
      } catch {
        payload = '[unserializable]';
      }
      emit('window:postmessage', { origin: origin.replace(/^https?:\/\//, ''), data: payload });
    },
    true
  );

  try {
    const origPush = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);
    window.history.pushState = function (state, title, url) {
      emit('history:push', { url: String(url || '') });
      return origPush(state, title, url);
    };
    window.history.replaceState = function (state, title, url) {
      emit('history:replace', { url: String(url || '') });
      return origReplace(state, title, url);
    };
  } catch {}

  try {
    const desc = Object.getOwnPropertyDescriptor(window.location, 'href');
    if (desc && desc.configurable) {
      Object.defineProperty(window.location, 'href', {
        configurable: true,
        get() {
          return desc.get?.call(window.location);
        },
        set(v) {
          emit('location:href', { url: String(v).slice(0, 120) });
          desc.set?.call(window.location, v);
        },
      });
    }
  } catch {}
}
